import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export function useReports() {
  const { authAxios, token } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const { t } = useTranslation();
  const [reportData, setReportData] = useState(null);
  const [procurementData, setProcurementData] = useState(null);
  const [accountingData, setAccountingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState("30days");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchOrdersReport = useCallback(async (period) => {
    try {
      let url = `/reports/orders?period=${period}`;
      if (dateFrom && dateTo) url = `/reports/orders?date_from=${dateFrom}&date_to=${dateTo}`;
      const res = await authAxiosRef.current.get(url);
      setReportData(res.data);
    } catch { toast.error(t("reports.toast.loadOrdersError")); }
  }, [dateFrom, dateTo, t]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchProcurement = useCallback(async () => {
    try {
      const res = await authAxiosRef.current.get("/stock/procurement");
      setProcurementData(res.data);
    } catch { toast.error(t("reports.toast.loadProcurementError")); }
  }, [t]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchAccounting = useCallback(async (period) => {
    try {
      let url = `/reports/accounting/summary?period=${period}`;
      if (dateFrom && dateTo) url = `/reports/accounting/summary?date_from=${dateFrom}&date_to=${dateTo}`;
      const res = await authAxiosRef.current.get(url);
      setAccountingData(res.data);
    } catch { toast.error(t("reports.toast.loadAccountingError")); }
  }, [dateFrom, dateTo, t]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([
        fetchOrdersReport(selectedPeriod),
        fetchProcurement(),
        fetchAccounting(selectedPeriod),
      ]);
      setLoading(false);
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePeriodChange = async (period) => {
    setSelectedPeriod(period);
    setLoading(true);
    await Promise.all([fetchOrdersReport(period), fetchAccounting(period)]);
    setLoading(false);
  };

  const handleCustomReport = async (onClose) => {
    onClose();
    if (!dateFrom || !dateTo) return toast.error(t("reports.toast.selectDates"));
    setLoading(true);
    await Promise.all([fetchOrdersReport(selectedPeriod), fetchAccounting(selectedPeriod)]);
    setLoading(false);
    toast.success(t("reports.toast.reportGenerated"));
  };

  const downloadCsv = (endpoint, filename) => {
    const API = process.env.REACT_APP_BACKEND_URL;
    const url = `${API}/api${endpoint}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", filename);
        link.click();
        URL.revokeObjectURL(blobUrl);
        toast.success(`${t("reports.toast.download")}: ${filename}`);
      })
      .catch(() => toast.error(t("reports.toast.downloadError")));
  };

  const getCsvParams = () =>
    dateFrom && dateTo
      ? `date_from=${dateFrom}&date_to=${dateTo}`
      : `period=${selectedPeriod}`;

  return {
    reportData,
    procurementData,
    accountingData,
    loading,
    selectedPeriod,
    dateFrom,
    dateTo,
    setDateFrom,
    setDateTo,
    handlePeriodChange,
    handleCustomReport,
    downloadCsv,
    getCsvParams,
  };
}
